import { GetString } from 'fluent-react';
import React, { CSSProperties, useState } from 'react';
import styled from 'styled-components';
import {
  PolicyRecommendation,
} from '../../graphQL/types/shared';
import { failIfValidOrNonExhaustive } from '../../Utils';
import { policyColors } from './Utils';

//#region Styling
const RootBase = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  color: var(--foreground-color);
  background-color: var(--background-color);
  pointer-events: auto;
  cursor: pointer;

  @media (max-width: 1050px) {
    max-width: 45%;
    opacity: 0.75;
  }
`;
const ActiveRoot = styled(RootBase)`
  border-radius: 20px;
  padding: 4px 10px;
`;
const InactiveRoot = RootBase;
const LabelBase = styled.div`
  line-height: 1.2;

  &:hover {
    cursor: pointer;
  }

  @media (max-width: 1050px) {
    font-size: 1.3vw;
  }

  @media (max-width: 720px) {
    font-size: 0.7rem;
  }
`;
const ActiveLabel = LabelBase;
const InactiveLabel = styled(LabelBase)`
  border-bottom: dashed 1px var(--foreground-color);
`;
const IconOuterContainer = styled.div`
  width: 12px;
  height: 18px;

  polygon {
    fill: var(--foreground-color);
  }
`;
const Tooltip = styled.div`
  position: absolute;
  background-color: white;
  border: 1px solid var(--policy-color);
  color: var(--policy-color);
  padding: 4px 10px;
  width: 14.5rem;
`;
//#endregion

// Allow CSS custom properties
declare module 'csstype' {
  interface Properties {
    '--foreground-color'?: string;
    '--policy-color'?: string;
  }
}

interface Props {
  policy: PolicyRecommendation;
  activePolicy: PolicyRecommendation;
  getFluentString: GetString;
  openFlowChart: (value: PolicyRecommendation | null) => void;
}

const QuadrantLabel = (props: Props) => {
  const {policy, getFluentString, activePolicy, openFlowChart} = props;
  const isActive = (policy === activePolicy);
  const RootComponent = isActive ? ActiveRoot : InactiveRoot;
  const Label = isActive ? ActiveLabel : InactiveLabel;
  const separationFromChartEdge = 10; // in px;
  const iconSeparationFromLabel = 5; // in px
  const tooltipSeparationFromLabel = 5; // in px

  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);

  let label: string;
  let labelTop: number | undefined, labelBottom: number | undefined;
  let labelLeft: number | undefined, labelRight: number | undefined;
  let tooltipTop: string | undefined, tooltipBottom: string | undefined;
  let tooltipLeft: string | undefined, tooltipRight: string | undefined;
  let flexDirection: CSSProperties['flexDirection'];
  let iconStyle: React.CSSProperties;
  let policyDescription: string;
  if (policy === PolicyRecommendation.ParsimoniousIndustrial) {
    label = getFluentString('strategic-approach-name-parsimonious-industrial');
    labelTop = labelLeft = separationFromChartEdge;
    labelBottom = labelRight = undefined;
    flexDirection = 'row';
    iconStyle = {
      marginRight: `${iconSeparationFromLabel}px`,
    };
    policyDescription = getFluentString('strategic-approach-explanation-parsimonious-industrial');
    tooltipTop = `calc(100% + ${tooltipSeparationFromLabel}px)`;
    tooltipLeft = '0';
    tooltipBottom = tooltipRight = undefined;
  } else if (policy === PolicyRecommendation.StrategicBets) {
    label = getFluentString('strategic-approach-name-strategic-bets');
    labelBottom = labelLeft = separationFromChartEdge;
    labelTop = labelRight = undefined;
    flexDirection = 'row';
    iconStyle = {
      marginRight: `${iconSeparationFromLabel}px`,
    };
    policyDescription = getFluentString('strategic-approach-explanation-strategic-bets');
    tooltipBottom = `calc(100% + ${tooltipSeparationFromLabel}px)`;
    tooltipLeft = '0';
    tooltipTop = tooltipRight = undefined;
  } else if (policy === PolicyRecommendation.LightTouch) {
    label = getFluentString('strategic-approach-name-light-touch');
    labelTop = labelRight = separationFromChartEdge;
    labelBottom = labelLeft = undefined;
    flexDirection = 'row-reverse';
    iconStyle = {
      marginLeft: `${iconSeparationFromLabel}px`,
    };
    policyDescription = getFluentString('strategic-approach-explanation-light-touch');
    tooltipTop = `calc(100% + ${tooltipSeparationFromLabel}px)`;
    tooltipRight = '0';
    tooltipBottom = tooltipLeft = undefined;
  } else if (policy === PolicyRecommendation.TechFrontier) {
    label = getFluentString('strategic-approach-name-tech-frontier');
    labelBottom = labelRight = separationFromChartEdge;
    labelTop = labelLeft = undefined;
    flexDirection = 'row-reverse';
    iconStyle = {
      marginLeft: `${iconSeparationFromLabel}px`,
    };
    policyDescription = getFluentString('strategic-approach-explanation-tech-frontier');
    tooltipBottom = `calc(100% + ${tooltipSeparationFromLabel}px)`;
    tooltipRight = '0';
    tooltipTop = tooltipLeft = undefined;
  } else {
    failIfValidOrNonExhaustive(policy, 'Invalid policy name ' + policy);
    // These lines will never be executed:
    label = '';
    labelTop = labelRight = labelBottom = labelLeft = undefined;
    flexDirection = 'row';
    iconStyle = {};
    policyDescription = '';
    tooltipTop = tooltipRight = tooltipBottom = tooltipLeft = undefined;
  }
  const iconColor = isActive ? 'white' : '#666';
  const policyColor = policyColors[policy];
  const style: React.CSSProperties = {
    'top': labelTop, 'right': labelRight, 'bottom': labelBottom, 'left': labelLeft,
    flexDirection,
    '--icon-color': iconColor,
    '--foreground-color': isActive ? 'white' : policyColor,
    '--background-color': isActive ? policyColor : 'white',
  };

  let tooltip: React.ReactElement<any> | null;
  if (isTooltipShown === true) {
    const tooltipStyle: React.CSSProperties = {
      '--policy-color': policyColor,
      'top': tooltipTop,
      'right': tooltipRight,
      'bottom': tooltipBottom,
      'left': tooltipLeft,
    };
    tooltip = (
      <Tooltip style={tooltipStyle}>{policyDescription}</Tooltip>
    );
  } else {
    tooltip = null;
  }

  const onMouseEnter = () => setIsTooltipShown(true);
  const onMouseLeave = () => setIsTooltipShown(false);

  return (
    <RootComponent
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={() => openFlowChart(policy)}
    >
      <IconOuterContainer style={iconStyle} dangerouslySetInnerHTML={{__html: require('./policy.svg')}}/>
      <Label >{label}</Label>
      {tooltip}
    </RootComponent>
  );
};

export default QuadrantLabel;
